import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { communityAPI } from '../services/api';
import type { CommunityProfile } from '../types';
import '../styles/community.css';

const Community: React.FC = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<CommunityProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categories = useMemo(
    () => ['All', 'Employee Engagement', 'Wellbeing', 'HR and Payroll', 'Contracts and Legal', 'Recruitment'],
    []
  );
  const [category, setCategory] = useState<(typeof categories)[number]>('All');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await communityAPI.listPublic();
        setProfiles(res.data || []);
      } catch (e: any) {
        console.error('Community list load error:', e);
        const status = e?.response?.status;
        const msg = e?.response?.data?.message || e?.message;
        if (status) {
          setError(`${status}: ${msg || 'Request failed'}`);
        } else {
          setError(msg || 'Failed to load community profiles');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    return profiles.filter((p) => {
      if (category === 'All') return true;
      const services = Array.isArray(p.services) ? p.services : [];
      return services.some((s) => s?.toLowerCase() === category.toLowerCase());
    });
  }, [profiles, category]);

  if (loading) {
    return (
      <Box className="community-list__loading" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="community-list">
      <Box className="community-list__hero">
        <Container maxWidth="lg" className="community-list__hero-container">
          <Box className="community-list__header">
            <Typography className="community-list__title" variant="h2" component="h1">
              Connect with the
              <br />
              engagement community
            </Typography>
            <Box className="community-list__header-actions">
              <Button className="community-list__create" variant="outlined" onClick={() => navigate('/community/submit')}>
                Create your profile
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" className="community-list__content" sx={{ py: { xs: 4, md: 6 } }}>
        <Box className="community-list__controls">
          {error && <Alert className="community-list__error" severity="error">{error}</Alert>}

          <Typography className="community-list__filters-label" variant="caption">
            FILTER BY
          </Typography>

          <Stack className="community-list__filters" direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {categories.map((c) => {
              const active = c === category;
              return (
                <Chip
                  key={c}
                  className={`community-list__filter${active ? ' community-list__filter--active' : ''}`}
                  label={c}
                  onClick={() => setCategory(c)}
                  variant="filled"
                />
              );
            })}
          </Stack>
        </Box>

        <Box className="community-list__results" sx={{ mt: 3 }}>
          {filtered.length === 0 ? (
            <Typography className="community-list__empty" variant="body1" color="text.secondary">
              No profiles found.
            </Typography>
          ) : (
            <Grid className="community-list__grid" container spacing={3}>
              {filtered.map((p) => {
                const location = [p.location?.city, p.location?.country].filter(Boolean).join(', ');
                return (
                  <Grid className="community-list__item" item xs={12} sm={6} md={4} key={p._id}>
                    <Card className="community-card" sx={{ height: '100%' }}>
                      <CardActionArea className="community-card__action" onClick={() => navigate(`/community/${p._id}`)} sx={{ height: '100%' }}>
                        <CardContent className="community-card__content" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box className="community-card__header" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {p.logoDataUrl ? (
                              <Box
                                component="img"
                                src={p.logoDataUrl}
                                alt={p.displayName}
                                className="community-card__logo"
                              />
                            ) : (
                              <Box className="community-card__logo community-card__logo--placeholder" />
                            )}
                            <Box className="community-card__headings" sx={{ minWidth: 0 }}>
                              <Typography className="community-card__name" variant="h6" noWrap>
                                {p.displayName}
                              </Typography>
                              {location ? (
                                <Typography className="community-card__meta" variant="body2" color="text.secondary" noWrap>
                                  {location}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>

                          <Box sx={{ mt: 2, flexGrow: 1 }}>
                            {p.summary ? (
                              <Typography className="community-card__summary" variant="body2" color="text.secondary">
                                {p.summary}
                              </Typography>
                            ) : null}
                          </Box>

                          <Typography className="community-card__link" variant="body2" sx={{ mt: 2 }}>
                            View Profile
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Container>

      <Box className="community-list__footer site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography
                className="site-footer__branddesc"
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}
              >
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2} />
            <Grid className="site-footer__col" item xs={12} md={2} />
            <Grid className="site-footer__col" item xs={12} md={2} />
          </Grid>
        </Container>

        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box className="site-footer__bar-inner" sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}>
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">
                  © {new Date().getFullYear()} SuperGauge
                </Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">Contact</Typography>
                  <Typography className="site-footer__link" variant="caption">Terms</Typography>
                  <Typography className="site-footer__link" variant="caption">Privacy policy</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Community;
